import { useMemo } from "react";
import { GuestType } from "@/types/enums/GuestType";
import type { LayoutVariant } from "./guestDetailLayout";
import {
  GuestDetailLayout,
  EntityDetailLayout,
  NamedEntityDetailLayout,
  NamedGuestDetailLayout,
} from "@/types/Event";
import {
  useEntityDetailLayout,
  useResolvedEntityDetailLayout,
} from "@/components/DetailLayoutConfig/detailLayouts";

/**
 * The admin CREATE forms whose field layout is configurable — a Standard
 * layout plus named VERSIONS per lane, stored on the event under
 * `detailLayouts.<surface>` (see {@link EventDetailLayouts}). The key IS the
 * storage entity key, so nothing has to be mapped on the way to the API.
 *
 * Deliberately NOT derived from the dialog's `companion`/`mainGuest` props:
 * those two flags cannot tell the guest-list's "add companion" mount apart
 * from the companion-REQUEST lane, and the two are different lanes with
 * different layouts. Every mount passes its surface explicitly.
 */
export type CreateSurface =
  | "guestCreate"
  | "registrantCreate"
  | "companionCreate";

export const CREATE_SURFACES: readonly CreateSurface[] = [
  "guestCreate",
  "registrantCreate",
  "companionCreate",
];

/**
 * The two DECISION surfaces: the guest popup opened on a still-PENDING request
 * from the Anfragen page (or on a pending row anywhere else).
 *
 * They ride the same storage as the create lanes — `detailLayouts.<surface>`,
 * same read/save/CAS machinery — but resolve against the DETAIL field
 * vocabulary: this popup shows a guest that EXISTS, so credentials and the
 * display-only built-ins (`origin`, `createdAt`, `numCompanions`, …) are real
 * cells here, and "how many companions does this request bring" is decision
 * input rather than a blank on a form.
 *
 * ⚠ They are deliberately NOT the create lanes. One document cannot serve both:
 * an admin trims the add-request FORM to four fields as a matter of course, and
 * the same `hidden` list would then blind the surface where the registrant's
 * submitted answers are approved.
 */
export type RequestDetailSurface = "registrantDetail" | "companionDetail";

export const REQUEST_DETAIL_SURFACES: readonly RequestDetailSurface[] = [
  "registrantDetail",
  "companionDetail",
];

/** Every lane stored under `Event.detailLayouts.<surface>` (create + request). */
export type EntityLaneSurface = CreateSurface | RequestDetailSurface;

/**
 * Which FIELD vocabulary a lane resolves against — the one axis on which the
 * request lanes differ from the create lanes (see {@link RequestDetailSurface}).
 * Keyed off the suffix rather than a second table so a new lane cannot be added
 * to one and forgotten in the other.
 */
export function variantOfSurface(surface: EntityLaneSurface): LayoutVariant {
  return surface.endsWith("Create") ? "create" : "detail";
}

/**
 * CREATE value semantics — the one thing that decides whether an
 * additional-field DEFAULT is painted into an empty cell
 * (`additionalFieldValue`). True only for a create FORM rendered for a guest
 * that does not exist yet.
 *
 * Split out as a pure function on purpose: as an inline expression it was a
 * boolean default nothing could pin, and flipping it stayed green through 198
 * tests. On an existing guest a default would show a value the database does
 * not hold and cannot be cleared; on a create form its absence hides a value
 * the backend will inject on save. Both directions are wrong, so both are
 * pinned.
 */
export function isCreateSemantics(
  surface: EntityLaneSurface | undefined,
  existingGuest: boolean,
): boolean {
  return !!surface && variantOfSurface(surface) === "create" && !existingGuest;
}

/** URL segment ↔ surface. The storage keys are camelCase, which reads badly in
 *  a path — the builder route uses these short slugs instead. */
const SLUG_BY_SURFACE: Record<EntityLaneSurface, string> = {
  guestCreate: "guest",
  registrantCreate: "registrant",
  companionCreate: "companion",
  // The request lanes live under their OWN route
  // (`/guest-request-layout-builder/...`), so they may reuse the short slugs —
  // the route prefix, not the slug, says which lane family is meant.
  registrantDetail: "registrant",
  companionDetail: "companion",
};

const CREATE_SURFACE_BY_SLUG: Record<string, CreateSurface> =
  Object.fromEntries(
    CREATE_SURFACES.map((surface) => [SLUG_BY_SURFACE[surface], surface]),
  );

const REQUEST_SURFACE_BY_SLUG: Record<string, RequestDetailSurface> =
  Object.fromEntries(
    REQUEST_DETAIL_SURFACES.map((surface) => [SLUG_BY_SURFACE[surface], surface]),
  );

export function slugOfCreateSurface(surface: EntityLaneSurface): string {
  return SLUG_BY_SURFACE[surface];
}

/** Route param → surface. Unknown/absent slug returns undefined so the page
 *  can render its not-found panel instead of silently editing the guest lane. */
export function createSurfaceOfSlug(
  slug: string | undefined,
): CreateSurface | undefined {
  return slug ? CREATE_SURFACE_BY_SLUG[slug] : undefined;
}

/** Route param → request-decision surface, same hard-stop contract as
 *  {@link createSurfaceOfSlug}. */
export function requestSurfaceOfSlug(
  slug: string | undefined,
): RequestDetailSurface | undefined {
  return slug ? REQUEST_SURFACE_BY_SLUG[slug] : undefined;
}

/**
 * Which request lane a PENDING guest is decided on — the same lane its create
 * dialog on the Anfragen page uses, so configuring "Anfrage — Begleitung"
 * changes both the companion create form and the companion request popup.
 *
 * The guest TYPE decides, not the page: a pending companion opened from the
 * guest list is still a companion request, and the two lanes may differ.
 */
export function requestSurfaceOfGuestType(
  guestType: string | undefined,
): RequestDetailSurface {
  return guestType === GuestType.COMPANION
    ? "companionDetail"
    : "registrantDetail";
}

/** i18n keys (namespace `guest-details`) naming each lane in the switcher,
 *  the builder title and the "copy from" list. */
export const CREATE_SURFACE_LABEL_KEY: Record<EntityLaneSurface, string> = {
  guestCreate: "layoutSurfaceCreate",
  registrantCreate: "layoutSurfaceRegistrant",
  companionCreate: "layoutSurfaceCompanion",
  registrantDetail: "layoutSurfaceRegistrantDetail",
  companionDetail: "layoutSurfaceCompanionDetail",
};

/** An entity-layout document as the GUEST layout resolver wants it. The two
 *  shapes share `groups`/`hidden` field-for-field; the create lanes never
 *  carry `tabs`/`actions`, so nothing is lost in the narrowing (`layouts` is
 *  read through {@link toGuestNamed} instead, one entry at a time). */
export function toGuestLayout(
  entity: EntityDetailLayout | undefined,
): GuestDetailLayout | undefined {
  if (!entity) return undefined;
  return { groups: entity.groups ?? [], hidden: entity.hidden };
}

/** The lane's NAMED versions in the guest shape. Same narrowing as
 *  {@link toGuestLayout}, `id`/`name` carried through — the builder's switcher
 *  and its whole-array CAS writes speak the guest type. */
export function toGuestNamed(
  entries: NamedEntityDetailLayout[] | undefined,
): NamedGuestDetailLayout[] {
  return (entries ?? []).map((e) => ({
    id: e.id,
    name: e.name,
    groups: e.groups ?? [],
    hidden: e.hidden,
  }));
}

/** The inverse: what goes back on the wire for `detailLayouts.<surface>.layouts`.
 *  ONLY `groups`/`hidden` — both strip vocabularies are empty server-side for
 *  these entities, so a stray `sidebar`/`actions` (or their entity twins) is a
 *  400 rather than a no-op. */
export function toEntityNamed(
  entries: NamedGuestDetailLayout[],
): NamedEntityDetailLayout[] {
  return entries.map((e) => ({
    id: e.id,
    name: e.name,
    groups: e.groups,
    hidden: e.hidden ?? [],
  }));
}

/**
 * Read ONE create lane's layout off the shared event cache entry — the same
 * subscription the builder writes through, so a save lands in every open
 * create dialog at once.
 *
 * `layout` is the SELECTED version: the user's pick for the current view
 * context, else the Standard (unnamed) layout — the same precedence engine the
 * entity popups use ({@link selectEntityDetailLayout}), so a dangling pick
 * falls back to Standard instead of blanking the dialog. `standard` and
 * `named` are the raw material the builder edits.
 *
 * `ready` is what a WRITER must gate on: an absent `layout` means both "this
 * lane has no layout" and "the read has not answered yet", and seeding a
 * builder draft from the second case would overwrite a real stored layout with
 * the default on the next save. Reading is safe without it (the dialog just
 * renders the default layout, which is what it rendered before this feature).
 */
export function useCreateLayout(
  eventId: string | null | undefined,
  surface: EntityLaneSurface,
): {
  layout: GuestDetailLayout | undefined;
  /** The unnamed Standard layout of this lane, whatever is selected. */
  standard: GuestDetailLayout | undefined;
  /** The lane's named versions ([] when none). */
  named: NamedGuestDetailLayout[];
  /** EDL_STANDARD or the selected named id. */
  selectedId: string;
  ifMatch: string | undefined;
  isPending: boolean;
  isFetching: boolean;
  ready: boolean;
  isError: boolean;
} {
  const doc = useEntityDetailLayout(eventId, surface);
  const resolved = useResolvedEntityDetailLayout(eventId, surface);
  const layout = useMemo(
    () => toGuestLayout(resolved.layout),
    [resolved.layout],
  );
  const standard = useMemo(() => toGuestLayout(doc.layout), [doc.layout]);
  const named = useMemo(() => toGuestNamed(doc.named), [doc.named]);
  return {
    layout,
    standard,
    named,
    selectedId: resolved.selectedId,
    ifMatch: doc.ifMatch,
    isPending: doc.isPending,
    isFetching: doc.isFetching,
    ready: doc.ready,
    isError: doc.isError,
  };
}
